import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../../components/Icon';
import type { PanelTab } from './RightPanel';

interface RightPanelStripProps {
  readonly tab: PanelTab;
  readonly onExpand: () => void;
  readonly onExpandToTab: (tab: PanelTab) => void;
}

const TABS: readonly { id: PanelTab; icon: IconName; labelKey: string }[] = [
  { id: 'arrange', icon: 'alignHorizontalLeft', labelKey: 'workspace.panel.arrange' },
  { id: 'filter', icon: 'filterList', labelKey: 'workspace.panel.filter' },
  { id: 'compose', icon: 'dashboardCustomize', labelKey: 'workspace.panel.compose' },
  { id: 'style', icon: 'palette', labelKey: 'workspace.panel.style' },
  { id: 'text', icon: 'stickyNote', labelKey: 'workspace.panel.text' },
  { id: 'discuss', icon: 'forum', labelKey: 'workspace.panel.discuss' }
];

// #402 — the collapsed right panel: an icon-only strip of the FILTER / EDIT / COMPOSE tabs.
// It collapses toward the right edge; the expand chevron points left. Clicking a tab icon
// expands the panel and selects that tab. Icons-over-words with `aria-label` + `title`.
export function RightPanelStrip({ tab, onExpand, onExpandToTab }: RightPanelStripProps) {
  const { t } = useTranslation();

  return (
    <div className="workspace-right-strip">
      <button
        type="button"
        className="icon-button workspace-right-toggle"
        aria-label={t('workspace.panel.expand')}
        title={t('workspace.panel.expand')}
        onClick={onExpand}
      >
        <Icon name="chevronLeft" />
      </button>

      <div className="workspace-right-strip-tabs" role="group" aria-label={t('workspace.panel.label')}>
        {TABS.map(({ id, icon, labelKey }) => (
          <button
            key={id}
            type="button"
            className={`icon-button ${tab === id ? 'active' : ''}`}
            aria-label={t(labelKey)}
            title={t(labelKey)}
            aria-pressed={tab === id}
            onClick={() => onExpandToTab(id)}
          >
            <Icon name={icon} />
          </button>
        ))}
      </div>
    </div>
  );
}
